import { memo, useCallback } from 'react';
import { Badge, Button, CartContainer } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowRight, BadgeCheck, RefreshCw, Zap } from 'lucide-react';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';
import StatusPill from './StatusPill.jsx';

// Native Enter/Space on the CTA <button> fires a keydown before the browser
// synthesizes the click, so it reaches CartContainer's own onKeyDown first —
// stopping only the click isn't enough. Same convention as EntityListCard.jsx.
const stopPropagation = (e) => e.stopPropagation();

const CAPABILITY_BADGE = {
  tool: { icon: Zap, variant: 'purple' },
  data_sync: { icon: RefreshCw, variant: 'success' },
  mcp: { variant: 'informative' },
};

/**
 * Marketplace card (CT-4167): the repeating card in the curated grids (CT-4164)
 * and Browse All (CT-4166). Distinct from the Connected card (CT-4161).
 *
 * Three connection states: `not_connected` → Connect CTA; `connected` → the
 * live `StatusPill` (no second CTA); `in_marketplace` → In Marketplace chip.
 * Source is `official` | `unofficial` | `partner` (partner gets its own badge
 * color). Whole card → Provider Detail via `onOpen(slug)`; the Connect CTA
 * also routes there in V1 and stops propagation (click + keydown) so it never
 * double-fires.
 */
function MarketplaceCard({ view, onOpen }) {
  const { t } = useTranslation('admin-studio');

  const handleOpen = useCallback(() => onOpen?.(view.slug), [onOpen, view.slug]);
  const handleCtaClick = useCallback(
    (e) => {
      e.stopPropagation();
      onOpen?.(view.slug);
    },
    [onOpen, view.slug],
  );

  const activeCapabilities = view.activeCapabilities ?? [];
  const usageParts = [];
  if (view.instancesCount > 1) {
    usageParts.push(t('integrations.card.instances', { count: view.instancesCount }));
  }
  if (view.callsPerDay != null) {
    usageParts.push(
      t('integrations.card.callsPerDay', { count: view.callsPerDay.toLocaleString() }),
    );
  }

  const sourceBadge =
    view.source !== 'partner' ? (
      <Badge variant="neutral" size="small" radius="full">
        {t(`integrations.marketplace.card.source.${view.source}`)}
      </Badge>
    ) : null;

  const footerState =
    view.connectionState === 'not_connected' ? (
      <Button
        variant="classicPrimary"
        size="sm"
        onClick={handleCtaClick}
        onKeyDown={stopPropagation}
      >
        {t('integrations.marketplace.card.connect')}
      </Button>
    ) : view.connectionState === 'in_marketplace' ? (
      <Badge variant="informative" size="small" radius="full">
        {t('integrations.marketplace.card.inMarketplace')}
      </Badge>
    ) : null;

  return (
    <CartContainer
      size="S"
      aria-label={t('integrations.card.openAria', { name: view.name })}
      onClick={handleOpen}
      className="relative min-h-[178px] gap-2.5 p-4"
    >
      <div className="absolute right-3.5 top-3.5 flex flex-col items-end gap-1">
        {view.connectionState === 'connected' && <StatusPill status={view.status} />}
        {view.source === 'partner' && (
          <Badge
            variant="purple"
            size="small"
            radius="default"
            className="h-[18px] px-1.5 text-[9px] font-bold uppercase tracking-[0.05em]"
          >
            {t('integrations.marketplace.card.source.partner')}
          </Badge>
        )}
      </div>

      <div className="flex items-start gap-3">
        <IntegrationProviderLogo slug={view.slug} name={view.name} size="medium" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-semibold text-content">{view.name}</p>
            {view.verified && (
              <BadgeCheck
                size={14}
                className="shrink-0 fill-blue-600 text-white dark:fill-blue-500"
                aria-label={t('integrations.marketplace.card.verified')}
              />
            )}
          </div>
          <p className="truncate text-xs text-content-muted">{view.vendor}</p>
        </div>
      </div>

      <p className="line-clamp-2 flex-1 text-xs leading-[1.55] text-content-muted">
        {view.description}
      </p>

      {activeCapabilities.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {activeCapabilities.map((type) => {
            const badge = CAPABILITY_BADGE[type] ?? { variant: 'neutral' };
            return (
              <Badge
                key={type}
                variant={badge.variant}
                icon={badge.icon}
                size="small"
                radius="default"
              >
                {t(`integrations.card.capability.${type}`)}
              </Badge>
            );
          })}
        </div>
      )}

      <div className="mt-1.5 flex min-h-[36px] items-center justify-between gap-2 border-t border-edge/50 pt-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {footerState}
          {sourceBadge}
          {usageParts.length > 0 && (
            <p className="truncate text-[11px] font-medium text-content-muted">
              {usageParts.join(' · ')}
            </p>
          )}
        </div>
        <ArrowRight size={14} className="shrink-0 text-content-muted" aria-hidden="true" />
      </div>
    </CartContainer>
  );
}

export default memo(MarketplaceCard);
